import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { isValidReferralCodeFormat, sanitizeReferralCode } from '@/utils/referralCodeUtils';

export const useBrokerReferralValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const { toast } = useToast();

  const validateReferralCode = async (code: string, currentBrokerId?: string): Promise<boolean> => {
    if (!code || code.trim() === '') {
      return true; // Empty codes are valid (will be auto-generated)
    }

    // Validate format first
    if (!isValidReferralCodeFormat(code)) {
      toast({
        title: 'Invalid Format',
        description: 'Referral code must be alphanumeric with no spaces or special characters (minimum 3 characters).',
        variant: 'destructive',
      });
      return false;
    }

    setIsValidating(true);
    try {
      const sanitizedCode = sanitizeReferralCode(code);
      const { data, error } = await supabase
        .from('broker_profiles')
        .select('id, company_name')
        .eq('referral_code', sanitizedCode)
        .neq('id', currentBrokerId || 'never-match');

      if (error) {
        console.error('Error validating referral code:', error);
        toast({
          title: 'Validation Error',
          description: 'Failed to validate referral code. Please try again.',
          variant: 'destructive',
        });
        return false;
      }

      if (data && data.length > 0) {
        toast({
          title: 'Referral Code Taken',
          description: `This referral code is already used by ${data[0].company_name || 'another broker'}. Please choose a different one.`,
          variant: 'destructive',
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating referral code:', error);
      toast({
        title: 'Validation Error',
        description: 'Failed to validate referral code. Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const generateReferralCode = async (companyName?: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.rpc('generate_broker_referral_code_from_name', {
        company_name: companyName || null
      });
      
      if (error) {
        console.error('Error generating referral code:', error);
        toast({
          title: 'Generation Error',
          description: 'Failed to generate referral code. Please try again.',
          variant: 'destructive',
        });
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error generating referral code:', error);
      toast({
        title: 'Generation Error',
        description: 'Failed to generate referral code. Please try again.',
        variant: 'destructive',
      });
      return null;
    }
  };

  const generateCodeFromName = async (companyName: string): Promise<string | null> => {
    return generateReferralCode(companyName);
  };

  return {
    validateReferralCode,
    generateReferralCode,
    generateCodeFromName,
    isValidating,
  };
};